import { useState, useMemo, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { useAuth } from '../AuthContext'
import { apiClient } from '../api/client'
import { PermissionRow } from '../api/admin'
import {
  getProjectNetworks,
  createNetwork,
  updateNetwork,
  deleteNetwork,
  type CommNetwork,
  type NetworkConfig
} from '../api/commNetworks'
import { getCommInterfaceTypes } from '../api/commInterfaceTypes'

const DETAIL_KIND_BADGES: Record<string, { bg: string; text: string }> = {
  lan: { bg: 'bg-blue-100', text: 'text-blue-800' },
  modbus: { bg: 'bg-amber-100', text: 'text-amber-800' },
  audio: { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  generic: { bg: 'bg-gray-100', text: 'text-gray-800' }
}

export default function ProjectNetworksPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  // Load interface types dynamically
  const { data: interfaceTypes = [] } = useQuery({
    queryKey: ['commInterfaceTypes'],
    queryFn: getCommInterfaceTypes
  })

  // Modals state
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingNetwork, setEditingNetwork] = useState<CommNetwork | null>(null)
  
  // Form values state
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [networkType, setNetworkType] = useState<CommNetwork['network_type']>('')
  const [description, setDescription] = useState('')
  
  // Config states based on detail_kind
  const [lanConfig, setLanConfig] = useState({
    subnet: '',
    gateway: '',
    vlan: '',
    dns: '',
    dhcp_range: ''
  })
  
  const [modbusConfig, setModbusConfig] = useState({
    baud_rate: '9600',
    parity: 'none' as 'none' | 'even' | 'odd',
    data_bits: '8',
    stop_bits: '1',
    protocol: 'modbus_rtu' as 'modbus_rtu' | 'modbus_ascii' | 'other'
  })
  
  const [audioConfig, setAudioConfig] = useState({
    signal_type: 'analog_balanced' as 'analog_balanced' | 'analog_unbalanced' | 'digital_aes_ebu' | 'digital_dante' | 'other',
    connector_type: 'xlr' as 'xlr' | 'jack_3_5' | 'jack_6_3' | 'phoenix' | 'other'
  })
  
  const [genericConfig, setGenericConfig] = useState({
    custom_params: ''
  })

  // Determine detail_kind from selected networkType
  const selectedInterfaceType = useMemo(() => {
    return interfaceTypes.find(t => t.code === networkType)
  }, [interfaceTypes, networkType])

  const detailKind = selectedInterfaceType?.detail_kind || 'generic'

  // Load project info
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  // Load project access permissions
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Check editing rights
  const canEdit = useMemo(() => {
    if (currentUser?.role === 'system_admin') return true
    if (!projectId || !currentUser?.id) return false

    // Check specific page permission
    const pagePermission = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        (p.resource_type === 'page:devices' || p.resource_type === 'page:device-instances') &&
        p.resource_id === null &&
        p.permission !== 'deny' &&
        (p.permission === 'edit' || p.permission === 'manage')
    )
    if (pagePermission) return true

    // Check project role inheritance
    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      return ['editor', 'project_admin'].includes(access.role_in_project)
    }

    return false
  }, [currentUser, projectId, allPermissions, projectAccessData])

  // Load networks query
  const { data: networks = [], isLoading: networksLoading } = useQuery({
    queryKey: ['projectNetworks', projectId],
    queryFn: () => getProjectNetworks(Number(projectId!)),
    enabled: !!projectId
  })

  // Keyboard escape modal close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeFormModal()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const closeFormModal = () => {
    setShowFormModal(false)
    setEditingNetwork(null)
    setName('')
    setCode('')
    setNetworkType(interfaceTypes[0]?.code || '')
    setDescription('')
    setLanConfig({ subnet: '', gateway: '', vlan: '', dns: '', dhcp_range: '' })
    setModbusConfig({ baud_rate: '9600', parity: 'none', data_bits: '8', stop_bits: '1', protocol: 'modbus_rtu' })
    setAudioConfig({ signal_type: 'analog_balanced', connector_type: 'xlr' })
    setGenericConfig({ custom_params: '' })
  }

  const handleOpenAdd = () => {
    setEditingNetwork(null)
    setName('')
    setCode('')
    setNetworkType(interfaceTypes[0]?.code || '')
    setDescription('')
    setShowFormModal(true)
  }

  const handleOpenEdit = (network: CommNetwork) => {
    setEditingNetwork(network)
    setName(network.name)
    setCode(network.code)
    setNetworkType(network.network_type)
    setDescription(network.description || '')
    
    // Fill configuration states
    const config = network.network_config || {}
    const matchedType = interfaceTypes.find(t => t.code === network.network_type)
    const matchedDetailKind = matchedType?.detail_kind || 'generic'
    
    if (matchedDetailKind === 'lan') {
      setLanConfig({
        subnet: config.subnet || '',
        gateway: config.gateway || '',
        vlan: config.vlan || '',
        dns: config.dns || '',
        dhcp_range: config.dhcp_range || ''
      })
    } else if (matchedDetailKind === 'modbus') {
      setModbusConfig({
        baud_rate: config.baud_rate || '9600',
        parity: (config.parity as any) || 'none',
        data_bits: config.data_bits || '8',
        stop_bits: config.stop_bits || '1',
        protocol: (config.protocol as any) || 'modbus_rtu'
      })
    } else if (matchedDetailKind === 'audio') {
      setAudioConfig({
        signal_type: (config.signal_type as any) || 'analog_balanced',
        connector_type: (config.connector_type as any) || 'xlr'
      })
    } else if (matchedDetailKind === 'generic') {
      setGenericConfig({
        custom_params: config.custom_params || ''
      })
    }
    
    setShowFormModal(true)
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: createNetwork,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectNetworks', projectId] })
      closeFormModal()
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при создании сети: ' + (err?.response?.data?.error || err.message))
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateNetwork(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectNetworks', projectId] })
      closeFormModal()
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при сохранении сети: ' + (err?.response?.data?.error || err.message))
    }
  })

  const deleteMutation = useMutation({
    mutationFn: deleteNetwork,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectNetworks', projectId] })
    },
    onError: (err: any) => {
      console.error(err)
      alert('Ошибка при удалении сети: ' + (err?.response?.data?.error || err.message))
    }
  })

  const handleDelete = (id: number, name: string) => {
    if (confirm(`Вы уверены, что хотите удалить сеть "${name}"?`)) {
      deleteMutation.mutate(id)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return alert('Укажите название сети')
    if (!code.trim()) return alert('Укажите код сети')
    if (!/^[a-zA-Z0-9_-]+$/.test(code.trim())) {
      return alert('Код сети может содержать только английские буквы, цифры, дефис и подчеркивание')
    }

    // Prepare network config depending on detail_kind
    let configData: NetworkConfig = {}
    if (detailKind === 'lan') {
      configData = lanConfig
    } else if (detailKind === 'modbus') {
      configData = modbusConfig
    } else if (detailKind === 'audio') {
      configData = audioConfig
    } else if (detailKind === 'generic') {
      configData = genericConfig
    }

    const payload = {
      project_id: Number(projectId!),
      network_type: networkType,
      name: name.trim(),
      code: code.trim(),
      description: description.trim() || undefined,
      network_config: configData
    }

    if (editingNetwork) {
      updateMutation.mutate({
        id: editingNetwork.id,
        data: {
          network_type: payload.network_type,
          name: payload.name,
          code: payload.code,
          description: payload.description,
          network_config: payload.network_config
        }
      })
    } else {
      createMutation.mutate(payload)
    }
  }

  const renderConfigDetails = (network: CommNetwork) => {
    const config = network.network_config || {}
    const matchedType = interfaceTypes.find(t => t.code === network.network_type)
    const matchedDetailKind = matchedType?.detail_kind || 'generic'

    if (matchedDetailKind === 'lan') {
      return (
        <div className="text-xs text-gray-500 space-y-0.5">
          {config.subnet && <div><span className="font-medium text-gray-700">Подсеть:</span> {config.subnet}</div>}
          {config.gateway && <div><span className="font-medium text-gray-700">Шлюз:</span> {config.gateway}</div>}
          {config.vlan && <div><span className="font-medium text-gray-700">VLAN:</span> {config.vlan}</div>}
          {config.dns && <div><span className="font-medium text-gray-700">DNS:</span> {config.dns}</div>}
          {config.dhcp_range && <div><span className="font-medium text-gray-700">DHCP:</span> {config.dhcp_range}</div>}
        </div>
      )
    } else if (matchedDetailKind === 'modbus') {
      const parityLabels: Record<string, string> = { none: 'None', even: 'Even', odd: 'Odd' }
      return (
        <div className="text-xs text-gray-500 space-y-0.5">
          <div><span className="font-medium text-gray-700">Baud Rate:</span> {config.baud_rate || '9600'}</div>
          <div><span className="font-medium text-gray-700">Parity:</span> {parityLabels[config.parity || 'none'] || config.parity}</div>
          <div><span className="font-medium text-gray-700">Data/Stop bits:</span> {config.data_bits || '8'}/{config.stop_bits || '1'}</div>
          <div><span className="font-medium text-gray-700">Protocol:</span> {config.protocol === 'modbus_rtu' ? 'Modbus RTU' : config.protocol === 'modbus_ascii' ? 'Modbus ASCII' : 'Other'}</div>
        </div>
      )
    } else if (matchedDetailKind === 'audio') {
      const signalTypeLabels: Record<string, string> = {
        analog_balanced: 'Analog Balanced',
        analog_unbalanced: 'Analog Unbalanced',
        digital_aes_ebu: 'AES/EBU',
        digital_dante: 'Dante',
        other: 'Other'
      }
      return (
        <div className="text-xs text-gray-500 space-y-0.5">
          <div><span className="font-medium text-gray-700">Сигнал:</span> {signalTypeLabels[config.signal_type || ''] || config.signal_type || '—'}</div>
          <div><span className="font-medium text-gray-700">Разъем:</span> <span className="uppercase">{config.connector_type || '—'}</span></div>
        </div>
      )
    } else if (matchedDetailKind === 'generic') {
      return (
        <div className="text-xs text-gray-500 line-clamp-3 whitespace-pre-wrap">
          {config.custom_params || '—'}
        </div>
      )
    }
    return <span className="text-gray-400">—</span>
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Link to="/projects" className="hover:text-blue-600">Проекты</Link>
          <span>/</span>
          <Link to={`/projects/${projectId}`} className="hover:text-blue-600">{project?.name || 'Проект'}</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">Сети проекта</span>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Сети проекта</h1>
            <p className="text-gray-600 mt-1">
              Управление коммуникационными сетями проекта для последующей привязки к ним интерфейсов оборудования
            </p>
          </div>
          {canEdit && (
            <button
              onClick={handleOpenAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 flex items-center gap-1.5 shadow-sm"
            >
              <span>➕ Добавить сеть</span>
            </button>
          )}
        </div>
      </div>

      {/* Main List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {networksLoading ? (
          <div className="text-center py-12 text-gray-500 font-medium">Загрузка списка сетей...</div>
        ) : networks.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Код
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Тип сети
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Конфигурация
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Описание
                  </th>
                  {canEdit && (
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {networks.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <code className="bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-gray-800 font-mono text-xs">
                        {item.code}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(() => {
                        const matched = interfaceTypes.find(t => t.code === item.network_type)
                        const kind = matched?.detail_kind || 'generic'
                        const badge = DETAIL_KIND_BADGES[kind] || DETAIL_KIND_BADGES.generic
                        return (
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
                            {matched?.name || item.network_type}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {renderConfigDetails(item)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {item.description || <span className="text-gray-300">нет описания</span>}
                    </td>
                    {canEdit && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleOpenEdit(item)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(item.id, item.name)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-3">🌐</div>
            <div className="text-lg font-medium">В этом проекте ещё не создано ни одной сети</div>
            <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
              Создайте сети проекта (например, LAN управления, шину RS-485 или аудио линки), чтобы связывать их с интерфейсами устройств.
            </p>
          </div>
        )}
      </div>

      {/* Add / Edit Form Modal */}
      {showFormModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeFormModal()
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 pb-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editingNetwork ? 'Редактировать сеть' : 'Добавить новую сеть'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название сети <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="например, LAN управления AV"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Код сети (eng) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="например, lan-mgmt"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                    required
                  />
                  <span className="text-[10px] text-gray-400 mt-1 block">Уникальный ID (буквы, цифры, - и _)</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Тип сети <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={networkType}
                    onChange={(e) => setNetworkType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  >
                    {interfaceTypes.map(t => (
                      <option key={t.id} value={t.code}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Укажите назначение или особенности сети"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Dynamic Config Settings Block */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Настройки спецификации сети</h4>

                {detailKind === 'lan' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Подсеть (subnet)</label>
                        <input
                          type="text"
                          value={lanConfig.subnet}
                          onChange={(e) => setLanConfig(prev => ({ ...prev, subnet: e.target.value }))}
                          placeholder="10.73.90.0/24"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Шлюз (gateway)</label>
                        <input
                          type="text"
                          value={lanConfig.gateway}
                          onChange={(e) => setLanConfig(prev => ({ ...prev, gateway: e.target.value }))}
                          placeholder="10.73.90.1"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">VLAN ID</label>
                        <input
                          type="text"
                          value={lanConfig.vlan}
                          onChange={(e) => setLanConfig(prev => ({ ...prev, vlan: e.target.value }))}
                          placeholder="90"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Серверы DNS</label>
                        <input
                          type="text"
                          value={lanConfig.dns}
                          onChange={(e) => setLanConfig(prev => ({ ...prev, dns: e.target.value }))}
                          placeholder="8.8.8.8, 1.1.1.1"
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Диапазон DHCP</label>
                      <input
                        type="text"
                        value={lanConfig.dhcp_range}
                        onChange={(e) => setLanConfig(prev => ({ ...prev, dhcp_range: e.target.value }))}
                        placeholder="10.73.90.100 - 10.73.90.250"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      />
                    </div>
                  </div>
                )}

                {detailKind === 'modbus' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Baud Rate (Скорость)</label>
                        <select
                          value={modbusConfig.baud_rate}
                          onChange={(e) => setModbusConfig(prev => ({ ...prev, baud_rate: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        >
                          <option value="2400">2400 bps</option>
                          <option value="4800">4800 bps</option>
                          <option value="9600">9600 bps</option>
                          <option value="19200">19200 bps</option>
                          <option value="38400">38400 bps</option>
                          <option value="57600">57600 bps</option>
                          <option value="115200">115200 bps</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Parity (Четность)</label>
                        <select
                          value={modbusConfig.parity}
                          onChange={(e) => setModbusConfig(prev => ({ ...prev, parity: e.target.value as any }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        >
                          <option value="none">None (Нет)</option>
                          <option value="even">Even (Чет)</option>
                          <option value="odd">Odd (Нечет)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Data Bits</label>
                        <select
                          value={modbusConfig.data_bits}
                          onChange={(e) => setModbusConfig(prev => ({ ...prev, data_bits: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        >
                          <option value="8">8 bits</option>
                          <option value="7">7 bits</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Stop Bits</label>
                        <select
                          value={modbusConfig.stop_bits}
                          onChange={(e) => setModbusConfig(prev => ({ ...prev, stop_bits: e.target.value }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        >
                          <option value="1">1 bit</option>
                          <option value="2">2 bits</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Protocol</label>
                        <select
                          value={modbusConfig.protocol}
                          onChange={(e) => setModbusConfig(prev => ({ ...prev, protocol: e.target.value as any }))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        >
                          <option value="modbus_rtu">Modbus RTU</option>
                          <option value="modbus_ascii">Modbus ASCII</option>
                          <option value="other">Other / Custom</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {detailKind === 'audio' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Тип сигнала</label>
                      <select
                        value={audioConfig.signal_type}
                        onChange={(e) => setAudioConfig(prev => ({ ...prev, signal_type: e.target.value as any }))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      >
                        <option value="analog_balanced">Analog Balanced</option>
                        <option value="analog_unbalanced">Analog Unbalanced</option>
                        <option value="digital_aes_ebu">Digital AES/EBU</option>
                        <option value="digital_dante">Digital Dante (IP)</option>
                        <option value="other">Other / Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Тип разъема</label>
                      <select
                        value={audioConfig.connector_type}
                        onChange={(e) => setAudioConfig(prev => ({ ...prev, connector_type: e.target.value as any }))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      >
                        <option value="xlr">XLR</option>
                        <option value="jack_3_5">Jack 3.5mm</option>
                        <option value="jack_6_3">Jack 6.3mm</option>
                        <option value="phoenix">Phoenix terminal block</option>
                        <option value="other">Other / Custom</option>
                      </select>
                    </div>
                  </div>
                )}

                {detailKind === 'generic' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Кастомные параметры</label>
                    <textarea
                      value={genericConfig.custom_params}
                      onChange={(e) => setGenericConfig(prev => ({ ...prev, custom_params: e.target.value }))}
                      rows={3}
                      placeholder="Укажите любые параметры в свободном формате..."
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                    />
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeFormModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm font-semibold"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
